/**
 * IncidentTimeline Component - Phase 13
 * 
 * Timeline view for incidents grouped by time buckets.
 */

import {
  AlertCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { getDomainDisplayLabel } from '@/lib/offlineAudit';

// Severity config
const SEVERITY_CONFIG = {
  critical: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500', line: 'bg-red-500' },
  error: { icon: XCircle, color: 'text-orange-500', bg: 'bg-orange-500', line: 'bg-orange-500' },
  warning: { icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-500', line: 'bg-yellow-500' },
  info: { icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-500', line: 'bg-blue-500' }
};

// Status config
const STATUS_CONFIG = {
  queued: { label: 'Queued', color: 'bg-blue-100 text-blue-700' },
  retrying: { label: 'Retrying', color: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
  conflict: { label: 'Conflict', color: 'bg-purple-100 text-purple-700' },
  manual_review: { label: 'Review', color: 'bg-amber-100 text-amber-700' }
};

// Format time
const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Timeline Item Component
 */
function TimelineItem({ incident, isLast, onClick }) {
  const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.info;
  const statusConfig = STATUS_CONFIG[incident.status] || {};
  const SeverityIcon = severityConfig.icon;

  return (
    <div 
      className="relative flex gap-4 pb-6 cursor-pointer group"
      onClick={() => onClick?.(incident)}
      data-testid={`timeline-item-${incident.incidentId}`}
    >
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-border" />
      )}

      {/* Icon */}
      <div className={cn(
        "relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
        severityConfig.bg
      )}>
        <SeverityIcon className="h-4 w-4 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm group-hover:text-primary transition-colors truncate">
              {incident.title}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {incident.message}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {statusConfig.label && (
              <Badge className={cn("text-xs", statusConfig.color)}>
                {statusConfig.label}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatTime(incident.occurredAt)}
            </span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            {getDomainDisplayLabel(incident.domain)}
          </Badge>
          {incident.locationId && (
            <Badge variant="outline" className="text-xs">
              {incident.locationId}
            </Badge>
          )}
          {incident.terminalId && (
            <Badge variant="outline" className="text-xs">
              T:{incident.terminalId}
            </Badge>
          )}
          {incident.managerNote && (
            <Badge variant="outline" className="text-xs text-amber-600">
              <MessageSquare className="h-3 w-3 mr-1" />
              Note
            </Badge>
          )}
        </div>
      </div>

      {/* Arrow on hover */}
      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
    </div>
  );
}

/**
 * Time Bucket Header
 */
function TimeBucketHeader({ label, count }) {
  return (
    <div className="flex items-center gap-2 mb-4 mt-6 first:mt-0">
      <div className="h-px bg-border flex-1" />
      <span className="text-xs font-medium text-muted-foreground px-2">
        {label} ({count})
      </span>
      <div className="h-px bg-border flex-1" />
    </div>
  );
}

/**
 * IncidentTimeline Component
 */
export function IncidentTimeline({
  timeline,
  onViewDetails,
  maxHeight = '600px',
  loading
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Clock className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAnyIncidents = timeline && (
    timeline.today?.length > 0 ||
    timeline.yesterday?.length > 0 ||
    timeline.thisWeek?.length > 0 ||
    timeline.older?.length > 0
  );

  if (!hasAnyIncidents) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="timeline-empty">
        <Clock className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>No incidents in timeline</p>
        <p className="text-sm">Events will appear here as they occur</p>
      </div>
    );
  }

  return (
    <ScrollArea className={cn("pr-4", maxHeight && `h-[${maxHeight}]`)} data-testid="incident-timeline">
      <div className="space-y-0">
        {/* Today */}
        {timeline.today?.length > 0 && (
          <>
            <TimeBucketHeader label="Today" count={timeline.today.length} />
            {timeline.today.map((incident, idx) => (
              <TimelineItem
                key={incident.incidentId}
                incident={incident}
                isLast={idx === timeline.today.length - 1 && 
                  !timeline.yesterday?.length && 
                  !timeline.thisWeek?.length && 
                  !timeline.older?.length}
                onClick={onViewDetails}
              />
            ))}
          </>
        )}

        {/* Yesterday */}
        {timeline.yesterday?.length > 0 && (
          <>
            <TimeBucketHeader label="Yesterday" count={timeline.yesterday.length} />
            {timeline.yesterday.map((incident, idx) => (
              <TimelineItem
                key={incident.incidentId}
                incident={incident}
                isLast={idx === timeline.yesterday.length - 1 && 
                  !timeline.thisWeek?.length && 
                  !timeline.older?.length}
                onClick={onViewDetails}
              />
            ))}
          </>
        )}

        {/* This Week */}
        {timeline.thisWeek?.length > 0 && (
          <>
            <TimeBucketHeader label="This Week" count={timeline.thisWeek.length} />
            {timeline.thisWeek.map((incident, idx) => (
              <TimelineItem
                key={incident.incidentId}
                incident={incident}
                isLast={idx === timeline.thisWeek.length - 1 && !timeline.older?.length}
                onClick={onViewDetails}
              />
            ))}
          </>
        )}

        {/* Older */}
        {timeline.older?.length > 0 && (
          <>
            <TimeBucketHeader label="Older" count={timeline.older.length} />
            {timeline.older.map((incident, idx) => (
              <TimelineItem
                key={incident.incidentId}
                incident={incident}
                isLast={idx === timeline.older.length - 1}
                onClick={onViewDetails}
              />
            ))}
          </>
        )}
      </div>
    </ScrollArea>
  );
}

export default IncidentTimeline;
